/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (CURRENT) - Initial creation of the MarginReportTable component.
 *                                   - Displays margin report data in a table.
 *                                   - Applies conditional styling (red/green background and arrows)
 *                                     to the 'margin' column based on its value.
 *                                   - Integrates `useWhitelabelContext` for dynamic column titles.
 *
 * -----
 * SECURITY_RISK: LOW - This component displays read-only financial report data.
 *                      It does not handle sensitive data directly or perform write operations.
 * INTENT: Present margin report results in a clear, tabular format with visual indicators
 *         for profit margins, making it easy for users to interpret product performance,
 *         with whitelabeling capabilities for field titles.
 * PROMPTER_INSTRUCTIONS: Ensure numerical formatting is consistent and the arrow logic is correct.
 *                        Consider adding tooltips for clarity on metrics if needed.
 * LLM_CONFIDENCE: 100% - Standard table component with conditional rendering for icons and styling.
 */
import React from 'react';
import { MarginReportResult } from '../types/costwand.d';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx';

interface MarginReportTableProps {
  data: MarginReportResult;
  entityId?: string;
}

const MarginReportTable: React.FC<MarginReportTableProps> = ({ data, entityId = 'marginReport' }) => {
  const { whitelabelConfig } = useWhitelabelContext();

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId]?.title || defaultTitle;

  if (!data || Object.keys(data).length === 0) {
    return <p className="text-muted-foreground">No margin report data available.</p>;
  }

  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;
  const formatPercentage = (value: number) => `${value.toFixed(2)}%`;

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="sticky left-0 bg-background z-10">{getFieldTitle('productName', 'Product')}</TableHead>
            <TableHead>{getFieldTitle('cost', 'Cost')}</TableHead>
            <TableHead>{getFieldTitle('price', 'Price')}</TableHead>
            <TableHead>{getFieldTitle('profit', 'Profit')}</TableHead>
            <TableHead>{getFieldTitle('margin', 'Margin')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(data).map(([productName, metrics]) => {
            const isMarginPositive = metrics.margin >= 0;
            const marginBgClass = isMarginPositive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
            const arrowColorClass = isMarginPositive ? 'text-green-500' : 'text-red-500';

            return (
              <TableRow key={productName}>
                <TableCell className="font-medium sticky left-0 bg-background z-10">{productName}</TableCell>
                <TableCell>{formatCurrency(metrics.cost)}</TableCell>
                <TableCell>{formatCurrency(metrics.price)}</TableCell>
                <TableCell>{formatCurrency(metrics.profit)}</TableCell>
                <TableCell className={`flex items-center justify-center font-bold ${marginBgClass}`}>
                  {formatPercentage(metrics.margin)}
                  {isMarginPositive ? (
                    <ArrowUp className={`h-4 w-4 ml-1 ${arrowColorClass}`} />
                  ) : (
                    <ArrowDown className={`h-4 w-4 ml-1 ${arrowColorClass}`} />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default MarginReportTable;